import React, { Component, ErrorInfo, ReactNode } from 'react';
import styles from '../../styles/shared/ErrorBoundary.module.css';
import { STRING_CONSTANTS } from '../../constants/stringConstants';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional override label shown when an error is caught. */
  fallbackTitle?: string;
  /** Optional callback fired with the error + componentStack. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-level ErrorBoundary.
 *
 * Catches every JavaScript error thrown by descendants and renders a polished
 * fallback (DocX brand + reload + go to dashboard) instead of the React-default
 * white screen.
 *
 * History: introduced after the May-9 MyReviews regression, the data-guard
 * white-screen capture (DG-026), and three further runtime crashes verified
 * during the 2026-05-13 audit (Records.tsx, DoctorProfileView reviews.map,
 * AppointmentReport View Report). All of those would have rendered a fallback
 * here instead of breaking the whole app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Always log so the error appears in browser console / Sentry hook later.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info.componentStack);
    if (this.props.onError) {
      try {
        this.props.onError(error, info);
      } catch {
        /* swallow secondary error from the callback so we don't loop */
      }
    }
  }

  private handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  private handleGoHome = (): void => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className={styles.container} role="alert">
        <div className={styles.card}>
          <div className={styles.iconCircle}>
            <span className={styles.materialIcon} aria-hidden="true">
              {STRING_CONSTANTS.ICONS.HEALTH_AND_SAFETY}
            </span>
          </div>
          <h1 className={styles.title}>
            {this.props.fallbackTitle ?? STRING_CONSTANTS.MESSAGES.OOPS_THATS_ON_US}
          </h1>
          <p className={styles.subtitle}>
            {STRING_CONSTANTS.MESSAGES.OOPS_APPOINTMENT_SUBTITLE}
          </p>
          {this.state.error?.message ? (
            <details className={styles.details}>
              <summary className={styles.detailsSummary}>
                {STRING_CONSTANTS.MESSAGES.ERROR_BOUNDARY_DETAILS_SUMMARY}
              </summary>
              <code className={styles.errorMessage}>{this.state.error.message}</code>
            </details>
          ) : null}
          <div className={styles.actions}>
            <button type="button" className={styles.primaryButton} onClick={this.handleReload}>
              {STRING_CONSTANTS.MESSAGES.RELOAD_PAGE}
            </button>
            <button type="button" className={styles.secondaryButton} onClick={this.handleGoHome}>
              {STRING_CONSTANTS.MESSAGES.GO_TO_DASHBOARD}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
